
import React, { useState, useMemo } from 'react';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { Phase, Activity, Dependency } from '../types.ts';
import { Calendar, Plus, ChevronRight, ChevronDown, Trash2, CheckCircle2, GripVertical, AlertTriangle, Link as LinkIcon, X, Camera, Eye, ListFilter, BarChart3, Projector } from 'lucide-react';
import { useToast } from '../hooks/useToast.ts';
import { GanttChart } from './GanttChart.tsx';

export const ScheduleView: React.FC<{ projectId: number }> = ({ projectId }) => {
    // Note: Project ID is number in props, but string in API calls
    const phases = useStore(() => api.phases.list(p => p.projectId === projectId.toString()), [projectId], 'phases') || [];
    const activities = useStore(() => api.activities.list(a => a.projectId === projectId.toString()), [projectId], 'activities') || [];
    const dependencies = useStore(() => api.dependencies.list(d => d.projectId === projectId.toString()), [projectId], 'dependencies') || [];
    const { addToast } = useToast();

    const [newPhaseName, setNewPhaseName] = useState('');
    const [addingActivityToPhase, setAddingActivityToPhase] = useState<string | null>(null);
    const [newActivityName, setNewActivityName] = useState('');
    const [linkingActivityId, setLinkingActivityId] = useState<string | null>(null);

    // View State
    const [isLookaheadMode, setIsLookaheadMode] = useState(false);
    const [viewMode, setViewMode] = useState<'List' | 'Gantt'>('List');

    // Conflict Detection Logic
    const conflicts = useMemo(() => {
        const issues = new Map<string, string[]>();
        dependencies.forEach(dep => {
            const pred = activities.find(a => a.id === dep.predecessorActivityId);
            const succ = activities.find(a => a.id === dep.successorActivityId);
            if (pred && succ && dep.type === 'FS') {
                const predFinish = new Date(pred.finishDate).getTime();
                const succStart = new Date(succ.startDate).getTime();
                if (succStart < predFinish) {
                    const msgs = issues.get(succ.id) || [];
                    msgs.push(`Logic Break: Starts before ${pred.title} finishes.`);
                    issues.set(succ.id, msgs);
                }
            }
        });
        return issues;
    }, [activities, dependencies]);

    // Grouping & Filtering
    const grouped = useMemo(() => {
        const now = new Date();
        const twoWeeks = new Date();
        twoWeeks.setDate(now.getDate() + 14);

        return phases.sort((a, b) => a.orderIndex - b.orderIndex).map(phase => {
            let phaseActivities = activities.filter(a => a.phaseId === phase.id).sort((a, b) => a.orderIndex - b.orderIndex);

            if (isLookaheadMode) {
                phaseActivities = phaseActivities.filter(a => {
                    // Include if:
                    // 1. In Progress
                    // 2. Not Started but starts within window
                    if (a.status === 'In Progress') return true;
                    if (a.status === 'Not Started') {
                        const start = new Date(a.startDate);
                        return start <= twoWeeks;
                    }
                    return false;
                });
            }

            return {
                ...phase,
                activities: phaseActivities
            };
        }).filter(p => !isLookaheadMode || p.activities.length > 0);
    }, [phases, activities, isLookaheadMode]);


    const handleAddPhase = async () => {
        if (!newPhaseName.trim()) return;
        await api.phases.add({
            projectId: projectId.toString(),
            title: newPhaseName,
            orderIndex: phases.length,
            startDate: new Date().toISOString(),
            finishDate: new Date().toISOString(),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        } as Phase);
        setNewPhaseName('');
    }

    const handleAddActivity = async (phaseId: string) => {
        if (!newActivityName.trim()) return;
        await api.activities.add({
            projectId: projectId.toString(),
            phaseId: phaseId,
            title: newActivityName,
            status: 'Not Started',
            percentComplete: 0,
            originalDuration: 5,
            remainingDuration: 5,
            startDate: new Date().toISOString(),
            finishDate: new Date().toISOString(),
            varianceDays: 0,
            orderIndex: activities.filter(a => a.phaseId === phaseId).length,
            wbsCode: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        } as Activity);
        setNewActivityName('');
        setAddingActivityToPhase(null);
    }

    const handleLink = async (targetActivityId: string) => {
        if (!linkingActivityId || linkingActivityId === targetActivityId) return;
        const exists = dependencies.find(d => d.predecessorActivityId === linkingActivityId && d.successorActivityId === targetActivityId);
        if (exists) { setLinkingActivityId(null); return; }
        await api.dependencies.add({
            projectId: projectId.toString(),
            predecessorActivityId: linkingActivityId,
            successorActivityId: targetActivityId,
            type: 'FS',
            lag: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        } as Dependency);
        setLinkingActivityId(null);
    };

    const handleCreateBaseline = async () => {
        let count = 0;
        for (const act of activities) {
            await api.activities.update(act.id, {
                baselineStartDate: act.startDate,
                baselineFinishDate: act.finishDate,
                varianceDays: 0
            });
            count++;
        }
        addToast({ type: 'success', title: 'Baseline Created', message: `Snapshotted ${count} activities to new baseline.` });
    };

    const getVariance = (act: Activity) => {
        if (!act.baselineFinishDate) return null;
        const current = new Date(act.finishDate).getTime();
        const baseline = new Date(act.baselineFinishDate).getTime();
        const diffMs = current - baseline;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header Metrics */}
            <div className="flex items-center justify-between bg-white border border-zinc-100 p-8 rounded-[2.5rem] shadow-sm">
                <div className="flex items-center gap-6">
                    <div className="p-4 bg-black rounded-[1.5rem] text-white shadow-xl"><Calendar size={28} /></div>
                    <div>
                        <h3 className="text-2xl font-black text-black tracking-tight uppercase">Master Schedule</h3>
                        <p className="text-[10px] font-black uppercase text-zinc-400 tracking-widest mt-1">
                            {viewMode === 'Gantt' ? 'Interactive Gantt Chart' : (isLookaheadMode ? '2-Week Lookahead View' : 'Manual Mode • Conflict Alerts Active')}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setViewMode(viewMode === 'List' ? 'Gantt' : 'List')}
                        className={`p-3 border rounded-xl transition-all flex items-center gap-2 ${viewMode === 'Gantt' ? 'bg-black text-white border-black' : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'}`}
                        title="Toggle Gantt View"
                    >
                        <BarChart3 size={20} />
                        <span className="text-xs font-bold hidden md:inline">{viewMode === 'Gantt' ? "List View" : "Gantt Chart"}</span>
                    </button>

                    <button
                        onClick={() => setIsLookaheadMode(!isLookaheadMode)}
                        className={`p-3 border rounded-xl transition-all flex items-center gap-2 ${isLookaheadMode ? 'bg-black text-white border-black' : 'bg-zinc-50 border-zinc-200 text-zinc-600 hover:bg-zinc-100'}`}
                        title="Toggle 2-Week Lookahead"
                    >
                        <ListFilter size={20} />
                        {isLookaheadMode && <span className="text-xs font-bold pr-2">Active</span>}
                    </button>

                    <button onClick={handleCreateBaseline} className="p-3 bg-zinc-50 border border-zinc-200 text-zinc-600 rounded-xl hover:bg-zinc-100 transition-all flex items-center gap-2" title="Snapshot Logic as Baseline">
                        <Camera size={20} />
                    </button>
                    <div className="h-8 w-px bg-zinc-200 mx-2"></div>
                    <input
                        value={newPhaseName}
                        onChange={e => setNewPhaseName(e.target.value)}
                        placeholder="New Phase Name..."
                        className="bg-zinc-50 border-none rounded-xl px-4 py-3 text-sm font-bold focus:ring-2 focus:ring-black outline-none w-64"
                    />
                    <button onClick={handleAddPhase} className="p-3 bg-black text-white rounded-xl hover:scale-105 transition-transform"><Plus size={20} /></button>
                </div>
            </div>

            {linkingActivityId && (
                <div className="fixed top-24 left-1/2 -translate-x-1/2 bg-black text-white px-6 py-3 rounded-full shadow-2xl z-50 animate-in slide-in-from-top-4 flex items-center gap-4">
                    <span className="text-xs font-bold uppercase tracking-widest">Select Successor Activity...</span>
                    <button onClick={() => setLinkingActivityId(null)} className="p-1 hover:bg-zinc-800 rounded-full"><X size={14} /></button>
                </div>
            )}

            {/* View Rendering */}
            {viewMode === 'Gantt' ? (
                <GanttChart activities={isLookaheadMode ? grouped.flatMap(p => p.activities) : activities} dependencies={dependencies} projectId={projectId} />
            ) : (
                /* Schedule Hierarchy */
                <div className="space-y-6">
                    {grouped.length === 0 && (
                        <div className="text-center p-12 bg-zinc-50 border border-zinc-200 border-dashed rounded-[2rem]">
                            <p className="text-zinc-400 font-bold">
                                {isLookaheadMode ? "No upcoming activities found in the next 14 days." : "No phases defined. Add a phase to start scheduling."}
                            </p>
                        </div>
                    )}

                    {grouped.map(phase => (
                        <div key={phase.id} className="bg-white border border-zinc-200 rounded-[2rem] overflow-hidden shadow-sm">
                            {/* Phase Header */}
                            <div className="bg-zinc-50/80 p-6 flex items-center justify-between border-b border-zinc-100">
                                <div className="flex items-center gap-4">
                                    <GripVertical size={16} className="text-zinc-300 cursor-grab" />
                                    <h4 className="font-black text-lg uppercase tracking-tight">{phase.title}</h4>
                                    <span className="px-2 py-0.5 bg-zinc-200 text-zinc-500 rounded text-[9px] font-black uppercase tracking-widest">{phase.activities.length} {isLookaheadMode ? 'Upcoming' : 'Activities'}</span>
                                </div>
                                <button onClick={() => setAddingActivityToPhase(phase.id === addingActivityToPhase ? null : phase.id)} className="text-[10px] font-black uppercase tracking-widest flex items-center gap-2 px-4 py-2 bg-white border border-zinc-200 rounded-lg hover:border-black transition-colors">
                                    <Plus size={14} /> Add Activity
                                </button>
                            </div>

                            {/* Activities List */}
                            <div className="divide-y divide-zinc-50">
                                {phase.activities.map(activity => {
                                    const activeConflicts = conflicts.get(activity.id) || [];
                                    const preds = dependencies.filter(d => d.successorActivityId === activity.id);
                                    const variance = getVariance(activity);

                                    return (
                                        <div key={activity.id} className={`p-6 hover:bg-zinc-50 transition-colors flex items-center justify-between group relative ${linkingActivityId === activity.id ? 'bg-zinc-100' : ''}`}>

                                            {linkingActivityId && linkingActivityId !== activity.id && (
                                                <div
                                                    onClick={() => handleLink(activity.id)}
                                                    className="absolute inset-0 bg-black/5 cursor-pointer flex items-center justify-center z-10 opacity-0 hover:opacity-100 transition-opacity">
                                                    <span className="bg-black text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">Link as Successor</span>
                                                </div>
                                            )}

                                            <div className="flex items-center gap-6">
                                                <div className={`w-2 h-12 rounded-full ${activity.status === 'Completed' ? 'bg-emerald-400' :
                                                        activity.status === 'In Progress' ? 'bg-amber-400' : 'bg-zinc-200'
                                                    }`}></div>
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className="font-bold text-sm text-zinc-900">{activity.title}</div>
                                                        {activeConflicts.length > 0 && (
                                                            <div className="group/alert relative">
                                                                <AlertTriangle size={16} className="text-amber-500" />
                                                                <div className="absolute left-6 top-0 w-64 bg-black text-white text-[10px] p-3 rounded-xl hidden group-hover/alert:block z-50 shadow-xl">
                                                                    {activeConflicts.map((c, i) => <div key={i}>{c}</div>)}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {variance !== null && variance !== 0 && (
                                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${variance > 0 ? 'bg-rose-100 text-rose-600' : 'bg-emerald-100 text-emerald-600'}`}>
                                                                {variance > 0 ? `+${variance}d` : `${variance}d`}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center gap-4">
                                                        <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">{activity.originalDuration} Days</span>
                                                        <span className="text-[10px] font-medium text-zinc-400 uppercase tracking-wider">
                                                            {new Date(activity.startDate).toLocaleDateString()} — {new Date(activity.finishDate).toLocaleDateString()}
                                                        </span>
                                                        {preds.length > 0 && (
                                                            <div className="flex items-center gap-1">
                                                                <LinkIcon size={10} className="text-zinc-300" />
                                                                <span className="text-[9px] text-zinc-400">Linked to: {preds.length}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                                                <button onClick={() => setLinkingActivityId(activity.id)} className="p-2 text-zinc-300 hover:text-black hover:bg-zinc-100 rounded-lg" title="Link Dependency">
                                                    <LinkIcon size={16} />
                                                </button>

                                                <button
                                                    onClick={() => api.activities.update(activity.id, {
                                                        status: activity.status === 'Completed' ? 'Not Started' : 'Completed',
                                                        percentComplete: activity.status === 'Completed' ? 0 : 100
                                                    })}
                                                    className={`p-2 rounded-lg ${activity.status === 'Completed' ? 'text-emerald-500 bg-emerald-50' : 'text-zinc-300 hover:text-black'}`}
                                                >
                                                    <CheckCircle2 size={18} />
                                                </button>

                                                <button onClick={() => api.activities.delete(activity.id)} className="p-2 text-zinc-300 hover:text-rose-500"><Trash2 size={18} /></button>
                                            </div>
                                        </div>
                                    )
                                })}

                                {addingActivityToPhase === phase.id && (
                                    <div className="p-6 bg-zinc-50 animate-in slide-in-from-top-2">
                                        <div className="flex gap-4">
                                            <input
                                                autoFocus
                                                value={newActivityName}
                                                onChange={e => setNewActivityName(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && handleAddActivity(phase.id)}
                                                placeholder="Activity Description..."
                                                className="flex-1 bg-white border border-zinc-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-black outline-none"
                                            />
                                            <button onClick={() => handleAddActivity(phase.id)} className="bg-black text-white px-6 py-3 rounded-xl text-sm font-bold">Add</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
